import React, { useEffect } from 'react';
import {
  HiSignal, HiBuildingOffice, HiHeart, HiAcademicCap,
  HiFire, HiGlobeAlt, HiWrenchScrewdriver, HiShieldCheck
} from 'react-icons/hi2';

export default function Clients() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const industries = [
    { icon: <HiSignal className="w-5 h-5" />, name: 'Telecom', desc: 'High-performance standby backups and telemetry monitoring systems powering massive cell tower grids.' },
    { icon: <HiBuildingOffice className="w-5 h-5" />, name: 'Hospitality (Hotels)', desc: 'Super-silent generators and automatic synchronization panels for uninterrupted guest comfort and zero-flicker power.' },
    { icon: <HiHeart className="w-5 h-5" />, name: 'Healthcare (Hospitals)', desc: 'Automated emergency generator fleets and transfer switches to ensure life-saving equipment never loses power.' },
    { icon: <HiAcademicCap className="w-5 h-5" />, name: 'Education (Schools)', desc: 'Solar rooftop installations and silent backups supporting continuous learning, computer labs, and campus security.' },
    { icon: <HiFire className="w-5 h-5" />, name: 'Fuel Stations', desc: 'Compact 10kVA – 30kVA automated generator setups configured for high-reliability fuel delivery operations.' },
    { icon: <HiGlobeAlt className="w-5 h-5" />, name: 'Dairy/Farm', desc: 'Rugged power backups and solar water pumping solutions protecting delicate dairy processing and farming infrastructure.' },
    { icon: <HiWrenchScrewdriver className="w-5 h-5" />, name: 'Industrial', desc: 'Heavy-duty silent generators up to 3000kVA and synchronization panelboards powering manufacturing plants.' },
    { icon: <HiShieldCheck className="w-5 h-5" />, name: 'Government/PSU', desc: 'High-security standby power configurations backing public works, railway NOCs, municipal water pumps, and offices.' },
  ];

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/industries serve.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Sectors We Power</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Industries <span className="text-gradient-gold">We Serve</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Providing custom power engineering, silent backup generators, and grid-tied solar systems across critical national sectors.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <span>Home</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Industries Served</span>
        </div>
      </div>

      {/* Intro + Industry Grid */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <span className="eyebrow">Our Experience Range</span>
          <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mb-6">
            Powering Everything from Local Retail Outlets to National Cellular Grids
          </h2>
          <p className="text-base text-body leading-relaxed max-w-3xl mx-auto">
            Whether you are a small retail storefront requiring a compact, whisper-silent 10kVA backup generator, or a national telecommunications operator managing thousands of cell towers under strict 99.99% SLAs, Green Gusto has you covered. We deliver standard and comprehensive energy solutions tailored to keep both local commercial and heavy industrial operations running smoothly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((ind, i) => (
            <div
              key={i}
              className="bg-white border border-border/70 rounded-3xl p-8 shadow-card hover:shadow-premium hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-2xl card-icon flex items-center justify-center mb-6">
                {ind.icon}
              </div>
              <h4 className="text-base font-display font-extrabold text-heading mb-3">{ind.name}</h4>
              <p className="text-xs text-body leading-relaxed">{ind.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sector Callouts */}
      <section className="py-20 bg-light-section border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="eyebrow">Featured Projects</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading mb-3">Sector Callouts & Major Clients</h2>
            <p className="text-sm text-body leading-relaxed">
              Explore our capabilities and client footprint in major cellular network grids and high-capacity corporate/government projects.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* Telecom Callout */}
            <div className="bg-charcoal text-white border border-white/5 rounded-3xl p-8 shadow-premium relative overflow-hidden group hover:border-primary-200 transition-all duration-300">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(36,55,70,0.5),transparent_60%)]" />
              <div className="relative z-10">
                <span className="badge-gold text-[10px] mb-4 block w-fit">Featured Segment</span>
                <h3 className="text-xl font-display font-extrabold text-white mb-4 group-hover:text-primary transition-colors">Telecommunications Networks</h3>
                <p className="text-sm text-white/70 leading-relaxed mb-7">
                  Green Gusto is the preferred SLA partner for national network operators. We manage voltage regulation, GPS-routed maintenance dispatch, and preventative servicing for thousands of distributed cell nodes, securing 99.99% network uptime under extreme weather and remote conditions.
                </p>

              </div>
            </div>

            {/* Corporate/Government Callout */}
            <div className="bg-white text-heading rounded-3xl p-8 border border-border shadow-card relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(199,154,59,0.04),transparent_60%)]" />
              <div className="relative z-10">
                <span className="badge-primary text-[10px] mb-4 block w-fit">Featured Segment</span>
                <h3 className="text-xl font-display font-extrabold text-heading mb-4 group-hover:text-primary transition-colors">Corporate & Government Infrastructure</h3>
                <p className="text-sm text-body leading-relaxed mb-7">
                  From engineering high-yield solar rooftop plants to configuring heavy generator synchronizations and LT panel switchgear systems, we deliver public utility and high-security enterprise solutions. Our engineering compliance stands qualified by major industrial bodies and corporations.
                </p>


              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
